import { useQuery } from '@apollo/react-hooks'
import { Box, TablePagination, Typography, SelectChangeEvent } from '@mui/material'
import times from 'lodash/times'
import { ChangeEventHandler, useMemo, useState } from 'react'

import { TablePaginationActions, useScrollContext } from '../../../components'
import { ErrorViewer } from '../../../componentsPure'
import { CLAIMS_CREDITS } from './gql'
import GeoclaimCreditsItem from './Item'
import NoGeoclaimCredits from './NoItems'
import SelectType from './SelectType'

interface ClaimsCreditsResult {
  claimsCredits: {
    total: number
    page: number
    last: boolean
    data: {
      uid: string
      yyyymmdd: number
      credits: number
      type: string
    }[]
  }
}

const GeoclaimCreditsListContent: React.FC<React.PropsWithChildren<unknown>> = () => {
  const scrollElement = useScrollContext()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [type, setType] = useState('')
  
  const variables = useMemo(() => ({
    type: type || undefined,
    pagination: {
      page,
      perPage,
    },
    sort: {
      field: 'yyyymmdd',
      order: -1,
    },
  }), [page, perPage, type])
  
  const { data, loading, error } = useQuery<ClaimsCreditsResult>(CLAIMS_CREDITS, {
    variables,
  })

  const remoteRowCount = data?.claimsCredits?.total ?? 0
  const credits = data?.claimsCredits?.data ?? []

  const changePage = (p: number) => {
    if (scrollElement) {
      scrollElement.scrollTop = 0
    }
    setPage(p)
  }
  
  const handleChangePage = (_event: unknown, newPage: number) => {
    changePage(newPage + 1)
  }
  
  const handleChangeRowsPerPage: ChangeEventHandler<HTMLInputElement> = (event) => {
    setPerPage(parseInt(event.target.value, 10))
    changePage(1)
  }
  
  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setType(event.target.value)
    changePage(1)
  }

  if (error) {
    return <ErrorViewer error={error} />
  }

  return (
    <>
      <Box display="flex" alignItems="center" mb={2} justifyContent="flex-end" padding="8px">
        <SelectType type={type} onChange={handleTypeChange} />
        <Box marginLeft={2} textAlign="right">
          <Typography>Total Records: {remoteRowCount.toLocaleString()}</Typography>
        </Box>
      </Box>
      {loading ? (
        times(perPage).map((_, i) => (
          <GeoclaimCreditsItem
            key={i}
            uid=""
            yyyymmdd={20240101}
            credits={0}
            type="loading"
          />
        ))
      ) : credits.length ? (
        credits.map((credit, i) => (
          <GeoclaimCreditsItem
            key={i}
            uid={credit.uid}
            yyyymmdd={credit.yyyymmdd}
            credits={credit.credits}
            type={credit.type}
          />
        ))
      ) : (
        <NoGeoclaimCredits />
      )}
      {remoteRowCount > 0 ? (
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25]}
          count={remoteRowCount}
          rowsPerPage={variables.pagination.perPage}
          page={variables.pagination.page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      ) : null}
    </>
  )
}

export default GeoclaimCreditsListContent